import React, { useState, useEffect } from "react";
import { Box, Typography, Chip, IconButton } from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import JobDetailStepper from "./JobDetailStepper.js";
import axios from "axios";

const minimalGridSx = {
	height: "100%",
	width: "100%",
	border: "1px solid",
	borderColor: "divider",
	borderRadius: 2,
	bgcolor: "background.paper",
	"& .MuiDataGrid-main": {
		borderRadius: 2,
	},
	"& .MuiDataGrid-toolbarContainer": {
		padding: "10px 12px 6px",
		borderBottom: "1px solid",
		borderColor: "divider",
		gap: 1,
	},
	"& .MuiDataGrid-toolbarContainer .MuiButton-root": {
		minWidth: "auto",
		padding: "4px 8px",
		borderRadius: 1.5,
		color: "text.secondary",
		fontSize: "0.75rem",
		fontWeight: 500,
	},
	"& .MuiDataGrid-toolbarContainer .MuiInputBase-root": {
		borderRadius: 1.5,
		backgroundColor: "grey.50",
	},
	"& .MuiDataGrid-columnHeaders": {
		backgroundColor: "grey.50",
		borderBottom: "1px solid",
		borderColor: "divider",
	},
	"& .MuiDataGrid-columnHeader": {
		paddingX: 1.5,
	},
	"& .MuiDataGrid-columnHeaderTitle": {
		fontWeight: 500,
		color: "text.secondary",
		textTransform: "uppercase",
		fontSize: "0.7rem",
		letterSpacing: "0.4px",
	},
	"& .MuiDataGrid-columnHeader:focus": { outline: "none" },
	"& .MuiDataGrid-cell": {
		borderBottom: "1px solid",
		borderColor: "divider",
		padding: "6px 12px",
		fontSize: "0.82rem",
		color: "text.primary",
		display: "flex",
		alignItems: "center",
	},
	"& .MuiDataGrid-cell:focus": { outline: "none" },
	"& .MuiDataGrid-row": {
		cursor: "pointer",
		transition: "background-color 120ms ease",
		"&:hover": {
			bgcolor: "grey.50",
		},
	},
	"& .MuiDataGrid-columnSeparator": {
		display: "none",
	},
	"& .MuiDataGrid-footerContainer": {
		borderTop: "1px solid",
		borderColor: "divider",
		minHeight: 44,
	},
	"& .MuiDataGrid-virtualScroller": {
		overflowX: "auto",
	},
	"& .MuiDataGrid-withBorderColor": {
		borderColor: "divider",
	},
};

const ellipsisCellSx = {
	overflow: "hidden",
	textOverflow: "ellipsis",
	whiteSpace: "nowrap",
	fontWeight: 500,
	width: "100%",
};

const Jobs = () => {
	const [selectedJob, setSelectedJob] = useState(null);
	const [rows, setRows] = useState([]);
	const [loading, setLoading] = useState(false);

	const columns = [
		{
			field: "job_id",
			headerName: "Job ID",
			minWidth: 280,
			flex: 1.2,
			sortable: true,
			renderCell: (params) => (
				<Typography variant="body2" sx={ellipsisCellSx} title={params.value || "-"}>
					{params.value || "-"}
				</Typography>
			),
		},
		{
			field: "created_by",
			headerName: "Created By",
			flex: 1,
			minWidth: 240,
			sortable: true,
			renderCell: (params) => (
				<Typography variant="body2" sx={ellipsisCellSx} title={params.value || "-"}>
					{params.value || "-"}
				</Typography>
			),
		},
		{
			field: "job_type",
			headerName: "Type",
			minWidth: 140,
			flex: 0.7,
			sortable: true,
			renderCell: (params) => (
				<Typography variant="body2" sx={ellipsisCellSx} title={params.value || "-"}>
					{params.value || "-"}
				</Typography>
			),
		},
		{
			field: "createdAt",
			headerName: "Date Executed",
			minWidth: 220,
			flex: 0.9,
			sortable: true,
			valueGetter: (value) => (value ? new Date(value) : null),
			renderCell: (params) => (
				<Typography variant="body2" sx={ellipsisCellSx}>
					{params.row.createdAt ? new Date(params.row.createdAt).toLocaleString() : "-"}
				</Typography>
			),
		},
		{
			field: "status",
			headerName: "Status",
			minWidth: 150,
			flex: 0.7,
			sortable: true,
			renderCell: (params) => {
				let color = "default";
				const status = params.value?.toUpperCase();
				if (status === "SUCCESS" || status === "COMPLETED") color = "success";
				else if (status === "IN_PROGRESS" || status === "PENDING") color = "warning";
				else if (status === "FAILED" || status === "ERROR") color = "error";
				return (
					<Chip label={status} color={color} size="small" variant="outlined" sx={{ fontWeight: 600, borderRadius: "6px" }} />
				);
			},
		},
	];

	useEffect(() => {
		const fetchJobs = async () => {
			setLoading(true);
			try {
				const response = await axios.get("/api/pos_reco/get_all_job_logs");
				const logs = Array.isArray(response.data?.result?.logs) ? response.data.result.logs : [];
				setRows(
					logs.map((job, index) => ({
						...job,
						id: job?.id ?? job?.job_id ?? `job-${index}`,
					}))
				);
			} catch (error) {
				console.error("Failed to fetch jobs:", error);
			} finally {
				setLoading(false);
			}
		};

		fetchJobs();
	}, []);

	// Details View
	if (selectedJob) {
		return (
			<Box
				sx={{
					width: "100%",
					height: "100%",
					overflow: "auto",
					p: { xs: 2, md: 4 },
					boxSizing: "border-box",
					bgcolor: "background.paper",
					borderRadius: 2,
					border: "1px solid",
					borderColor: "divider",
				}}>
				<Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.5, mb: 3 }}>
					<IconButton
						onClick={() => setSelectedJob(null)}
						sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
						<ArrowBackRoundedIcon fontSize="small" />
					</IconButton>
					<Box sx={{ minWidth: 0, flex: 1 }}>
						<Typography variant="h6" fontWeight="600" color="text.primary">
							Job Details
						</Typography>
						<Typography variant="body2" color="text.secondary" noWrap title={selectedJob.job_id}>
							{selectedJob.job_id}
						</Typography>
					</Box>
					<Chip
						label={selectedJob.status || "UNKNOWN"}
						color={
							selectedJob.status?.toUpperCase() === "SUCCESS"
								? "success"
								: selectedJob.status?.toUpperCase() === "IN_PROGRESS"
									? "warning"
									: "error"
						}
						variant="outlined"
						size="small"
						sx={{ ml: 2, fontWeight: 600, borderRadius: "6px" }}
					/>
				</Box>
				<JobDetailStepper job={selectedJob} />
			</Box>
		);
	}

	// DataGrid Table View
	return (
		<Box
			sx={{
				width: "100%",
				height: "100%",
				display: "flex",
				flexDirection: "column",
				p: { xs: 2, md: 4 },
				boxSizing: "border-box",
				bgcolor: "background.paper",
				borderRadius: 2,
				border: "1px solid",
				borderColor: "divider",
				overflow: "hidden",
			}}>
			<Box sx={{ mb: 2.5 }}>
				<Typography variant="h6" fontWeight="600" color="text.primary">
					Jobs
				</Typography>
				<Typography variant="body2" color="text.secondary">
					Review upload and processing jobs, then open a row to inspect step-level progress.
				</Typography>
			</Box>
			<Box sx={{ flex: 1, minHeight: 0, width: "100%" }}>
				<DataGrid
					rows={rows}
					columns={columns}
					loading={loading}
					disableRowSelectionOnClick
					density="compact"
					pageSizeOptions={[10, 25, 50]}
					initialState={{
						pagination: { paginationModel: { pageSize: 10 } },
					}}
					slots={{
						toolbar: GridToolbar,
						noRowsOverlay: () => (
							<Typography variant="body2" sx={{ mt: 2 }}>
								No jobs found
							</Typography>
						),
					}}
					slotProps={{
						toolbar: { showQuickFilter: true },
					}}
					onRowClick={(params) => setSelectedJob(params.row)}
					sx={minimalGridSx}
				/>
			</Box>
		</Box>
	);
};

export default Jobs;
